import { FormControl, InputLabel, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import { useCallback, useMemo } from "react";
import { DefaultAutocomplete } from "../../../../../../common/components/default_autocomplete";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { EmpreendimentoDropDown } from "../../../../domain/models/empreendimento";

import CascateList from "../../../../../../common/components/cascate_list";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DefaultInput from "../../../../../../common/components/default_input";
import { DefaultSelectInput } from "../../../../../../common/components/default_select_input";

/**
 * Header
 */

type Props = {
  tipoPesquisa: number;
  valueField: string | null;
  statusDocumentos: number;
  resistenciaCompressao: number;
  statusEquipamentos: number;
  statusObras: number;
  centroCustosDropdown: CentroCustoDropDown[],
  empreendimentosDropdown: EmpreendimentoDropDown[],
  onTipoPesquisaChange: (tipoPesquisa: number) => void;
  onValueFieldChange: (valueField: string | null) => void;
  onStatusDocumentosChange: (statusDocumento: number) => void;
  onResistenciaCompressaoChange: (statusDocumento: number) => void;
  onStatusEquipamentosChange: (statusDocumento: number) => void;
  onStatusObrasChange: (statusObras: string) => void;
  onInformacaoClearFilters: () => void;
};

export const Header = ({
  tipoPesquisa,
  valueField,
  statusDocumentos,
  resistenciaCompressao,
  statusEquipamentos,
  statusObras,
  centroCustosDropdown,
  empreendimentosDropdown,
  onTipoPesquisaChange,
  onValueFieldChange,
  onStatusDocumentosChange,
  onResistenciaCompressaoChange,
  onStatusEquipamentosChange,
  onStatusObrasChange,
  onInformacaoClearFilters,
}: Props) => {

  const statusObrasOptions = useMemo(() => {
    return [
      { id: 3, label: "Todas" },
      { id: 0, label: "Em Andamento" },
      { id: 1, label: "Finalizadas" },
      { id: 2, label: "Paralisadas" },
    ];
  }, [
  ]);

  const selectedCentroCusto = useMemo(() => {
    if (tipoPesquisa !== 1 || valueField == null || valueField.length === 0) {
      return null;
    }
    return centroCustosDropdown.find(x => x.id === Number(valueField));
  }, [
    tipoPesquisa,
    valueField,
    centroCustosDropdown,
  ]);

  const selectedEmpreendimento = useMemo(() => {
    if (tipoPesquisa !== 2 || valueField == null || valueField.length === 0) {
      return null;
    }
    return empreendimentosDropdown.find(x => x.idEmpreendimento === Number(valueField));
  }, [
    tipoPesquisa,
    valueField,
    empreendimentosDropdown,
  ]);

  const handleTipoPesquisaChange = useCallback((e: SelectChangeEvent<number>) => {
    onTipoPesquisaChange(typeof e.target.value === 'string' ? Number(e.target.value) : e.target.value);
  }, [
    onTipoPesquisaChange,
  ]);

  const handleStatusDocumentosChange = useCallback((e: SelectChangeEvent<number>) => {
    onStatusDocumentosChange(typeof e.target.value === 'string' ? Number(e.target.value) : e.target.value);
  }, [
    onStatusDocumentosChange,
  ]);

  const handleResistenciaCompressaoChange = useCallback((e: SelectChangeEvent<number>) => {
    onResistenciaCompressaoChange(typeof e.target.value === 'string' ? Number(e.target.value) : e.target.value);
  }, [
    onResistenciaCompressaoChange,
  ]);

  const handleStatusEquipamentosChange = useCallback((e: SelectChangeEvent<number>) => {
    onStatusEquipamentosChange(typeof e.target.value === 'string' ? Number(e.target.value) : e.target.value);
  }, [
    onStatusEquipamentosChange,
  ]);

  return (
    <>
      <CascateList
        title="Filtros"
        defaultOpen>
        <div className="w-full flex flex-col gap-6 items-center justify-center">
          <div className="w-full grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 grid-flow-row-dense">
            <div className="flex col-span-1 md:col-span-2 lg:col-span-3 gap-1">
              <FormControl size="small" sx={{ width: "275px" }}>
                <InputLabel id="modulo-dashboard" size="small">Pesquisar por</InputLabel>
                <Select
                  size="small"
                  labelId="modulo-dashboard"
                  label="Pesquisar por"
                  value={tipoPesquisa}
                  onChange={handleTipoPesquisaChange}>
                  <MenuItem value={1}>Centro de Custo</MenuItem>
                  <MenuItem value={2}>Empreendimento</MenuItem>
                  <MenuItem value={3}>Número</MenuItem>
                  <MenuItem value={4}>Apresentação</MenuItem>
                </Select>
              </FormControl>

              <div className="w-full">
                {
                  tipoPesquisa === 1 &&
                  <DefaultAutocomplete
                    options={centroCustosDropdown}
                    getOptionKey={x => x.id}
                    getOptionLabel={x => x.title}
                    value={selectedCentroCusto ?? null}
                    onChange={v => onValueFieldChange(v !== null ? v.id.toString() : "")} />
                }
                {
                  tipoPesquisa === 2 &&
                  <DefaultAutocomplete
                    options={empreendimentosDropdown}
                    getOptionKey={x => x.idEmpreendimento}
                    getOptionLabel={x => x.descricao}
                    value={selectedEmpreendimento ?? null}
                    onChange={v => onValueFieldChange(v !== null ? v.idEmpreendimento.toString() : "")} />
                }
                {
                  (tipoPesquisa !== 1 && tipoPesquisa !== 2) &&
                  <DefaultInput
                    label="Pesquisar"
                    name="search_by_motivo_demissao"
                    value={valueField ?? ""}
                    onChangeValue={onValueFieldChange} />
                }
              </div>
            </div>

            <FormControl size="small" fullWidth>
              <InputLabel id="modulo-dashboard">
                Status Documentos
              </InputLabel>
              <Select
                labelId="modulo-dashboard"
                label="Status Documentos"
                value={statusDocumentos}
                onChange={handleStatusDocumentosChange}>
                <MenuItem value={0}>Todos</MenuItem>
                <MenuItem value={1}>Documentos não vencidos</MenuItem>
                <MenuItem value={2}>Documentos vencidos</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="modulo-dashboard">
                Resistência de compressão
              </InputLabel>
              <Select
                labelId="modulo-dashboard"
                label="Resistência de compressão"
                value={resistenciaCompressao}
                onChange={handleResistenciaCompressaoChange}>
                <MenuItem value={0}>Todos</MenuItem>
                <MenuItem value={1}>Pendentes</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="modulo-dashboard">
                Status Equipamentos
              </InputLabel>
              <Select
                labelId="modulo-dashboard"
                label="Status Equipamentos"
                value={statusEquipamentos}
                onChange={handleStatusEquipamentosChange}>
                <MenuItem value={0}>Todos</MenuItem>
                <MenuItem value={1}>Calibrados</MenuItem>
                <MenuItem value={2}>Verificados</MenuItem>
                <MenuItem value={3}>Em Atraso</MenuItem>
                <MenuItem value={4}>Equipamentos Locados em Atraso</MenuItem>
              </Select>
            </FormControl>

            <DefaultSelectInput
              name='statusObras'
              label='Status da Obra'
              showSelectionLabel={false}
              options={statusObrasOptions}
              value={statusObras}
              onChangeValue={onStatusObrasChange}
            />
          </div>
          <div className="w-full flex flex-row justify-end">
            <div className="w-[180px]">
              <PrimaryButton
                startIcon={<DynamicIcon nameIcon="ClearRounded" size={25} className="text-black" />}
                buttonColor="yellow"
                onClick={onInformacaoClearFilters}>
                Limpar filtros
              </PrimaryButton>
            </div>
          </div>
        </div>
      </CascateList>
    </>
  );
}